import React, { useState } from 'react';
import DeleteIcon from 'assets/Icons/delete/DeleteIcons';
import BackIcon from 'assets/Icons/modals/BackIcon';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import Modal from 'components/elements/Modal';
import useDeleteCard from './crud/useDelete';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateDeleteErrorMessage,
  generateDeleteSuccessMessage
} from 'utils/helpers';
import InfoBox from 'utils/InfoBox';
import IctService, { IctCredentialResponse } from 'services/hooks/ict-service';
import { useQuery } from 'react-query';
import { CardProps } from 'services/hooks/card-service';
import { CardFormat, colours } from 'utils/constants';
import { CircularProgress, Typography } from '@mui/material';

type DeleteProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteUUID: React.Dispatch<React.SetStateAction<string>>;
  deleteUUID?: string;
  deleteQuery: any;
  editQuery?: any;
  queryName: string;
};

export default function Delete({
  entityName,
  showModal,
  setShowModal,
  setDeleteUUID,
  deleteUUID,
  deleteQuery,
  editQuery,
  queryName
}: DeleteProps) {
  const [disabled, setDisabled] = useState(false);

  const { data: cardInfo } = useQuery<CardProps, Error>('card', () => editQuery(deleteUUID), {
    enabled: !!deleteUUID,
    cacheTime: 0,
    onError: () => {
      toast.error(generateApiFailErrorMessage('Card'));
    }
  });

  // Fetch card specific ICT credential data
  const { data: ictCredInfo, isLoading: isLoadingIctCredential } = useQuery<
    IctCredentialResponse,
    Error
  >('ictCredential', () => IctService.getIctCredential(deleteUUID), {
    enabled: !!deleteUUID && cardInfo?.format === CardFormat.ICT,
    cacheTime: 0,
    onError: () => {}
  });

  const deleteCardMutation = useDeleteCard({
    deleteQuery,
    queryName,
    removeOnSuccess: true
  });

  const handleDelete = () => {
    setDisabled(true);
    setShowModal(false);
    toast
      .promise(deleteCardMutation.mutateAsync(deleteUUID), {
        pending: 'Deleting Card...',
        success: generateDeleteSuccessMessage('Card'),
        error: {
          render({ data }: any) {
            const error = data as any;
            if (error.response && error.response.status === 400) {
              return (
                error.response.data?.error ||
                error.response.data?.message ||
                'An unexpected error occurred.'
              );
            }
            return generateDeleteErrorMessage('Card');
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        setDeleteUUID('');
        setDisabled(false);
      });
  };

  return (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setDeleteUUID) setDeleteUUID('');
        }}
      >
        <div className="flex flex-col items-center">
          <div className="delete-icon">
            <DeleteIcon />
          </div>
          <h1 className="mt-4">Are you sure?</h1>
          {cardInfo?.format === CardFormat.ICT && isLoadingIctCredential ? (
            <div className="flex items-center justify-center mb-3">
              <CircularProgress size={20} sx={{ color: colours.seafoam }} />
              <Typography variant="body1" style={{ marginLeft: '10px' }}>
                Verifying if the card has been consumed...
              </Typography>
            </div>
          ) : (
            cardInfo?.format === CardFormat.ICT &&
            ictCredInfo?.status === 'Consumed' && (
              <InfoBox
                message={`This ICT card has been consumed, deleting it will permanently revoke access, and the credential will no longer be usable. This action is irreversible.`}
                sx={{ textAlign: 'center' }}
              />
            )
          )}

          <p>{`Are you sure you want to delete this ${entityName.toLowerCase()}?`}</p>
          <div className="flex justify-between space-x-2">
            <SecondaryButton
              buttonLabel="Back"
              iconLeft={<BackIcon />}
              variant="default"
              size="px-4 w-full"
              onClick={() => {
                setShowModal(false);
                setDeleteUUID('');
              }}
            />
            <PrimaryButton
              buttonLabel="Delete"
              onClick={handleDelete}
              variant="default"
              size="px-4 w-auto gap-2"
              disabled={disabled || isLoadingIctCredential}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
